export type ViewPortKey = 'verticalCard' | 'horizontalCard' | 'tableView';

export type ViewPortMode = 'grid' | 'list' | 'table';

export interface AllProductsViewPorts {
  verticalCard: boolean;
  horizontalCard: boolean;
  tableView: boolean;
}

export const ALL_PRODUCTS_VIEW_PORTS_PAYLOAD_KEY = 'allProductsViewPorts';

export const VIEW_PORT_KEYS: ViewPortKey[] = ['verticalCard', 'horizontalCard', 'tableView'];

export const VIEW_PORT_KEY_TO_MODE: Record<ViewPortKey, ViewPortMode> = {
  verticalCard: 'grid',
  horizontalCard: 'list',
  tableView: 'table',
};

export const DEFAULT_ALL_PRODUCTS_VIEW_PORTS: AllProductsViewPorts = {
  verticalCard: true,
  horizontalCard: true,
  tableView: true,
};

export function isViewPortKey(value: unknown): value is ViewPortKey {
  return typeof value === 'string' && (VIEW_PORT_KEYS as string[]).includes(value);
}

export function parseAllProductsViewPorts(
  payload: Record<string, unknown> | null | undefined,
): AllProductsViewPorts {
  const stored = payload?.[ALL_PRODUCTS_VIEW_PORTS_PAYLOAD_KEY];
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
    return { ...DEFAULT_ALL_PRODUCTS_VIEW_PORTS };
  }

  const record = stored as Record<string, unknown>;
  return {
    verticalCard:
      typeof record['verticalCard'] === 'boolean'
        ? record['verticalCard']
        : DEFAULT_ALL_PRODUCTS_VIEW_PORTS.verticalCard,
    horizontalCard:
      typeof record['horizontalCard'] === 'boolean'
        ? record['horizontalCard']
        : DEFAULT_ALL_PRODUCTS_VIEW_PORTS.horizontalCard,
    tableView:
      typeof record['tableView'] === 'boolean'
        ? record['tableView']
        : DEFAULT_ALL_PRODUCTS_VIEW_PORTS.tableView,
  };
}

export function parseViewPortOrder(
  payload: Record<string, unknown> | null | undefined,
  fallback: ViewPortKey[] = VIEW_PORT_KEYS,
): ViewPortKey[] {
  const stored = payload?.[ALL_PRODUCTS_VIEW_PORTS_PAYLOAD_KEY];
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
    return [...fallback];
  }
  const order = (stored as Record<string, unknown>)['order'];
  if (!Array.isArray(order)) {
    return [...fallback];
  }
  const parsed = order.filter(isViewPortKey);
  return [...parsed, ...fallback.filter((key) => !parsed.includes(key))];
}

/**
 * Reads the configured default view-port key from layout payload.
 * Falls back to the first enabled view port when missing/invalid.
 */
export function parseDefaultViewPortKey(
  payload: Record<string, unknown> | null | undefined,
  viewPorts: AllProductsViewPorts = parseAllProductsViewPorts(payload),
  fallbackOrder: ViewPortKey[] = VIEW_PORT_KEYS,
): ViewPortKey {
  const stored = payload?.[ALL_PRODUCTS_VIEW_PORTS_PAYLOAD_KEY];
  if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
    const value = (stored as Record<string, unknown>)['default'];
    if (isViewPortKey(value)) {
      return value;
    }
  }
  return fallbackOrder.find((key) => viewPorts[key]) ?? fallbackOrder[0];
}

/** Maps a view-port key to the customer UI mode, only if that port is enabled. */
export function resolveDefaultViewMode(
  viewPorts: AllProductsViewPorts,
  defaultKey: ViewPortKey | null | undefined,
  order: ViewPortKey[] = VIEW_PORT_KEYS,
): ViewPortMode | null {
  const preferred = isViewPortKey(defaultKey) ? defaultKey : null;
  if (preferred && viewPorts[preferred]) {
    return VIEW_PORT_KEY_TO_MODE[preferred];
  }
  const firstEnabled = order.find((key) => viewPorts[key]);
  return firstEnabled ? VIEW_PORT_KEY_TO_MODE[firstEnabled] : null;
}
